<script context="module">
  // @ts-check
  /**
   * Valid featured item layouts for the CardLayout component.
   * @typedef {Object} VALID_FEATURED_LAYOUTS
   * @property {string} DoubleFeatured - Represents the double featured layout.
   * @property {string} SingleFullWidthFeature - Represents the single full-width feature layout.
   * @property {string} SingleTwoThirdsFeature - Represents the single two-thirds feature layout.
   */
  export const VALID_FEATURED_LAYOUTS = {
    DoubleFeatured: "doubleFeatured",
    SingleFullWidthFeature: "singleFullWidthFeature",
    SingleTwoThirdsFeature: "singleTwoThirdsFeature"
  };

  export const DEFAULT_LAYOUT = /** @type { const } */ "defaultLayout";
</script>

<script>
  // @ts-check
  import { setContext } from "svelte";
  import GridContainer from "../atoms/GridContainer.svelte";
  import Grid from "../atoms/Grid.svelte";
  import CardLayout, { VALID_LAYOUTS } from "./CardLayout.svelte";
  import { validatePropValue } from "../utilities/logs";
  import MasonryCardLayout, {
    MASONRY_VALID_LAYOUTS
  } from "./CardLayoutMasonry/CardLayoutMasonry.svelte";
  import {
    SPLIT_LAYOUT_VARIANTS,
    SPLIT_LAYOUT_RIGHT_GRID_COLUMNS
  } from "../organisms/SplitLayout.svelte";

  /**
   * @component CardLayoutMixed
   * @description A layout component that displays items in a grid layout with customizable number of columns and featured items.
   *
   * @prop {string} id - The ID of the component. Used for testing.
   * @prop {VALID_LAYOUTS[keyof VALID_LAYOUTS] | MASONRY_VALID_LAYOUTS[keyof MASONRY_VALID_LAYOUTS]} layout - The layout of the grid. Must be one of the values defined in VALID_LAYOUTS.
   * @prop {VALID_FEATURED_LAYOUTS[keyof VALID_FEATURED_LAYOUTS]} featuredLayout - The layout of the featured items in the grid. Must be one of the values defined in VALID_FEATURED_LAYOUTS.
   * @prop {*} component - The Svelte component to render for each item.
   * @prop {Array} items - The array of items to render.
   */

  /** Primarily used for testing
   * @type {string} */
  export let id = undefined;

  /** @type {VALID_LAYOUTS[keyof VALID_LAYOUTS] | MASONRY_VALID_LAYOUTS[keyof MASONRY_VALID_LAYOUTS]} */
  export let layout = VALID_LAYOUTS.TwoColumns;

  /** @type {VALID_FEATURED_LAYOUTS[keyof VALID_FEATURED_LAYOUTS]} */
  export let featuredLayout = VALID_FEATURED_LAYOUTS.SingleFullWidthFeature;

  /** The type of layout under the featured layout
   * @type { "default" | "masonry" }
   */
  export let variant = "default";

  /**
   * The split layout variant
   * @type  { SPLIT_LAYOUT_VARIANTS[keyof SPLIT_LAYOUT_VARIANTS] }
   */
  export let splitLayoutVariant = null;

  validatePropValue({
    componentName: "CardLayout",
    propName: "layout",
    propValue: layout,
    validPropValues: Object.values(VALID_LAYOUTS)
  });

  validatePropValue({
    componentName: "MasonryCardLayout",
    propName: "layout",
    propValue: layout,
    validPropValues: Object.values(MASONRY_VALID_LAYOUTS)
  });

  /** @type {typeof import("svelte").SvelteComponent<unknown>} */
  export let component;

  /** @type {Array<Object>} */
  export let items = [];

  const itemGridColumnClass = {
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_50_50]: {
      [VALID_FEATURED_LAYOUTS.DoubleFeatured]: "lg:col-span-3",
      [VALID_FEATURED_LAYOUTS.SingleFullWidthFeature]: "md:col-span-full",
      [VALID_FEATURED_LAYOUTS.SingleTwoThirdsFeature]:
        "md:col-span-6 lg:col-span-2 lg:[&:nth-last-of-type(2)]:col-span-4"
    },
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_25_75]: {
      [VALID_FEATURED_LAYOUTS.DoubleFeatured]: "md:col-span-3 lg:col-span-4",
      [VALID_FEATURED_LAYOUTS.SingleFullWidthFeature]: "md:col-span-full",
      [VALID_FEATURED_LAYOUTS.SingleTwoThirdsFeature]:
        "md:col-span-3 lg:col-span-2 lg:[&:nth-last-of-type(2)]:col-span-3"
    },
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_75_25]: {
      [VALID_FEATURED_LAYOUTS.DoubleFeatured]: "lg:col-span-2",
      [VALID_FEATURED_LAYOUTS.SingleFullWidthFeature]: "md:col-span-full",
      [VALID_FEATURED_LAYOUTS.SingleTwoThirdsFeature]:
        "md:col-span-2 lg:col-span-1 lg:[&:nth-last-of-type(2)]:col-span-2"
    },
    [DEFAULT_LAYOUT]: {
      [VALID_FEATURED_LAYOUTS.DoubleFeatured]: "md:col-span-6",
      [VALID_FEATURED_LAYOUTS.SingleFullWidthFeature]: "md:col-span-full",
      [VALID_FEATURED_LAYOUTS.SingleTwoThirdsFeature]:
        "md:col-span-6 lg:col-span-4 lg:[&:nth-last-of-type(2)]:col-span-8"
    }
  };

  /** @typedef {Object} GridConfig
   * @property {number} maxItemsPerFeaturedRow
   * @property {string} gridItemClasses
   */

  /** @type {Record<VALID_FEATURED_LAYOUTS[keyof VALID_FEATURED_LAYOUTS], GridConfig>} */
  const GRID_LAYOUTS = /** @type {const} **/ ({
    [VALID_FEATURED_LAYOUTS.DoubleFeatured]: {
      maxItemsPerFeaturedRow: 2,
      gridItemClasses: `col-span-4 relative max-md:pb-7.5 max-md:[&:not(:nth-last-child(2))]:pt-7.5 max-md:border-b-thin md:[&:nth-last-of-type(2)]:pb-0 ${itemGridColumnClass[splitLayoutVariant || DEFAULT_LAYOUT][featuredLayout]}`
    },
    [VALID_FEATURED_LAYOUTS.SingleFullWidthFeature]: {
      maxItemsPerFeaturedRow: 1,
      gridItemClasses: `col-span-4 max-md:pb-7.5 max-md:border-b-thin md:[&:nth-last-of-type(2)]:pb-0 ${itemGridColumnClass[splitLayoutVariant || DEFAULT_LAYOUT][featuredLayout]}`
    },
    [VALID_FEATURED_LAYOUTS.SingleTwoThirdsFeature]: {
      maxItemsPerFeaturedRow: 2,
      gridItemClasses: `col-span-4 relative max-md:pb-7.5 max-md:[&:not(:nth-last-child(2))]:pt-7.5 max-md:border-b-thin md:[&:nth-last-of-type(2)]:pb-0  ${itemGridColumnClass[splitLayoutVariant || DEFAULT_LAYOUT][featuredLayout]}`
    }
  });

  const isValidLayout = Object.values(VALID_FEATURED_LAYOUTS).includes(featuredLayout);

  const gridConfig = isValidLayout
    ? GRID_LAYOUTS[featuredLayout]
    : GRID_LAYOUTS[VALID_FEATURED_LAYOUTS.SingleFullWidthFeature];

  const { gridItemClasses, maxItemsPerFeaturedRow } = gridConfig;

  $: gridItems = {
    featuredItems: items.slice(0, maxItemsPerFeaturedRow),
    items: items.slice(maxItemsPerFeaturedRow)
  };

  $: hasLargeText = featuredLayout === VALID_FEATURED_LAYOUTS.SingleFullWidthFeature;

  setContext("CardLayoutMixed", { id, layout, component, featuredLayout });
</script>

{#if !!component && isValidLayout}
  <div {id} class="relative">
    <!-- Featured Items Grid -->
    {#if gridItems.featuredItems.length > 0}
      <GridContainer
        containerClass="h-full w-full"
        variant={splitLayoutVariant ? "bleed" : "regular"}
      >
        <Grid
          subGridColumns={SPLIT_LAYOUT_RIGHT_GRID_COLUMNS[splitLayoutVariant]}
          gridClass={gridItems.items.length ? "md:border-b-thin md:pb-7.5" : ""}
        >
          {#each gridItems.featuredItems as item, index}
            <div
              class="{gridItemClasses} after:absolute after:-right-grid-gutter-half after:top-0 after:h-full after:w-0 after:content-[''] lg:after:w-[1px] md:after:[&:not(:last-child)]:border-r-thin"
            >
              <svelte:component
                this={component}
                {...item}
                {index}
                isFeatured={true}
                {hasLargeText}
              />
            </div>
          {/each}
        </Grid>
      </GridContainer>
    {/if}
    <!-- / Featured Items Grid -->

    <!-- Remaining Items Grid -->
    {#if gridItems.items.length > 0}
      {#if variant === "masonry"}
        <MasonryCardLayout
          containerClass="mt-7.5"
          {component}
          {splitLayoutVariant}
          items={gridItems.items}
          {layout}
        />
      {:else}
        <CardLayout
          containerClass="mt-7.5"
          {component}
          {splitLayoutVariant}
          items={gridItems.items}
          {layout}
        />
      {/if}
    {/if}
    <!-- / Remaining Items Grid -->
  </div>
{/if}
